import { MembershipRoles } from "@/types/User"
import { Disclosure, Menu } from "@headlessui/react"
import { Bars3Icon, UserCircleIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useMemo } from "react"
import { Link, Outlet, useLocation } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { classNames } from "../../utils/classNames"

const userNavigation = [{ name: "Sign out", href: "/logout" }]

export const AppLayout = () => {
  const { loading, user, membership, toasts, orgId } = useApp()
  const { pathname } = useLocation()

  const navigation = useMemo(() => {
    const nav: any[] = []
    if (user?.superAdmin) {
      nav.push({ name: "Admin", href: "/private", current: false })
    }
    if (orgId) {
      nav.push({ name: "Dashboard", href: `/organizations/${orgId}/dashboard`, current: true })
      if (membership?.role === MembershipRoles.Admin) {
        nav.push({ name: "Settings", href: `/organizations/${orgId}/settings`, current: false })
      }
    }
    return nav
  }, [user, orgId, membership?.role])

  if (loading) {
    return null
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="rounded-lg bg-white p-8 text-center shadow-md">
          <h2 className="mb-4 text-xl font-semibold text-gray-900">Warning</h2>
          <p className="mb-4 text-gray-700">You must be logged in to access this page.</p>
          <Link to="/login" className="btn mx-auto w-fit">
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-full">
        <Disclosure as="nav" className="bg-white shadow-sm">
          {({ open }) => (
            <>
              <div className="max-w-10xl mx-auto px-4 sm:px-8 lg:px-10">
                <div className="flex h-16 justify-between">
                  <div className="flex">
                    <div className="flex shrink-0 items-center">
                      <img className="block h-8 w-auto lg:hidden" src="/icon.png" alt="Sierra Vet" />
                      <img className="hidden h-8 w-auto lg:block" src="/icon.png" alt="Sierra Vet" />
                    </div>
                    <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:items-center sm:space-x-3">
                      {navigation.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={classNames(
                            (item.href === "/" ? pathname === "/" : pathname.includes(item.href))
                              ? "bg-primary-100 text-primary-700"
                              : "text-gray-500 hover:text-gray-700",
                            "inline-flex items-center rounded-md px-3 py-2 text-sm font-medium",
                          )}
                          aria-current={
                            (item.href === "/" ? pathname === "/" : pathname.includes(item.href))
                              ? "page"
                              : undefined
                          }>
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                  <div className="hidden sm:ml-6 sm:flex sm:items-center">
                    {/* Profile dropdown */}
                    <Menu as="div" className="relative ml-3">
                      <div>
                        <Menu.Button className="focus:ring-primary-500 flex rounded-full bg-white text-sm focus:ring-2 focus:ring-offset-2 focus:outline-none">
                          <span className="sr-only">Open user menu</span>
                          <UserCircleIcon className="h-8 w-8 rounded-full text-gray-400 hover:text-gray-600" />
                        </Menu.Button>
                      </div>
                      <Menu.Items
                        transition
                        className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10 transition duration-200 ease-out focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0">
                        <Menu.Item as="div" className="flex flex-col border-b px-4 py-1">
                          <div className="text-sm font-medium text-gray-800">{user.name}</div>
                          <div className="text-xs font-medium text-gray-500">{user.email}</div>
                        </Menu.Item>
                        {userNavigation.map((item) => (
                          <Menu.Item key={item.name}>
                            {({ active }) => (
                              <a
                                href={item.href}
                                className={classNames(
                                  active ? "bg-gray-100" : "",
                                  "block px-4 py-2 text-sm text-gray-700",
                                )}>
                                {item.name}
                              </a>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Menu>
                  </div>
                  <div className="-mr-2 flex items-center sm:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="focus:ring-primary-500 inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:ring-2 focus:ring-offset-2 focus:outline-none">
                      <span className="sr-only">Open main menu</span>
                      {open ? (
                        <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="sm:hidden">
                <div className="space-y-1 pt-2 pb-3">
                  {navigation.map((item) => (
                    <Disclosure.Button
                      key={item.name}
                      as="a"
                      href={item.href}
                      className={classNames(
                        item.current
                          ? "border-primary-500 bg-primary-50 text-primary-700"
                          : "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800",
                        "block border-l-4 py-2 pr-4 pl-3 text-base font-medium",
                      )}
                      aria-current={item.current ? "page" : undefined}>
                      {item.name}
                    </Disclosure.Button>
                  ))}
                </div>
                <div className="border-t border-gray-200 pt-4 pb-3">
                  <div className="flex items-center px-4">
                    <div className="shrink-0"></div>
                    <div>
                      <div className="text-base font-medium text-gray-800">{user.name}</div>
                      <div className="text-sm font-medium text-gray-500">{user.email}</div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1">
                    {userNavigation.map((item) => (
                      <Disclosure.Button
                        key={item.name}
                        as="a"
                        href={item.href}
                        className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800">
                        {item.name}
                      </Disclosure.Button>
                    ))}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <div className="py-8">
          <main>
            <div className="max-w-10xl mx-auto sm:px-4">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </>
  )
}
